"use client";

import { useState, useEffect } from "react";
import { ShieldCheck } from "lucide-react";
import { ADSENSE_CLIENT_ID } from "@/lib/constants/adsense";

const CONSENT_KEY = "cookieConsentGranted";
const CONSENT_EXPIRY_KEY = "cookieConsentExpiry";
const CONSENT_DURATION_MS = 180 * 24 * 60 * 60 * 1000;
const AD_SCRIPT_ID = "adsense-init";

function loadAdsense() {
  if (!ADSENSE_CLIENT_ID) return;
  if (document.getElementById(AD_SCRIPT_ID)) return;
  const script = document.createElement("script");
  script.id = AD_SCRIPT_ID;
  script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT_ID}`;
  script.crossOrigin = "anonymous";
  script.async = true;
  document.head.appendChild(script);
}

function gtagConsentUpdate(consent: Record<string, string>) {
  if (typeof window !== "undefined" && (window as any).gtag) {
    (window as any).gtag("consent", "update", consent);
  }
}

// Microsoft Clarity consent signal — mirrors the gtag update so session
// recording cookies follow the same banner decision (GDPR).
function clarityConsentUpdate(granted: boolean) {
  if (typeof window !== "undefined" && (window as any).clarity) {
    (window as any).clarity("consent", granted);
  }
}

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY);
    const expiryStr = localStorage.getItem(CONSENT_EXPIRY_KEY);
    const isExpired = !expiryStr || Date.now() >= parseInt(expiryStr, 10);

    if (consent === "true" && !isExpired) {
      loadAdsense();
      setHasInteracted(true);
      return;
    }

    if (consent === "false" && !isExpired) {
      setHasInteracted(true);
      return;
    }

    setShowBanner(true);
  }, []);

  // Listen for custom event to reopen the cookie banner
  useEffect(() => {
    function handleOpenConsent() {
      localStorage.removeItem(CONSENT_KEY);
      localStorage.removeItem(CONSENT_EXPIRY_KEY);
      setShowBanner(true);
    }
    window.addEventListener("open-cookie-consent", handleOpenConsent);
    return () => window.removeEventListener("open-cookie-consent", handleOpenConsent);
  }, []);

  const handleAccept = () => {
    const expiry = Date.now() + CONSENT_DURATION_MS;
    localStorage.setItem(CONSENT_KEY, "true");
    localStorage.setItem(CONSENT_EXPIRY_KEY, expiry.toString());
    setShowBanner(false);
    setHasInteracted(true);
    loadAdsense();
    gtagConsentUpdate({
      ad_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "granted",
      analytics_storage: "granted",
    });
    clarityConsentUpdate(true);
  };

  const handleDecline = () => {
    const expiry = Date.now() + CONSENT_DURATION_MS;
    localStorage.setItem(CONSENT_KEY, "false");
    localStorage.setItem(CONSENT_EXPIRY_KEY, expiry.toString());
    setShowBanner(false);
    setHasInteracted(true);
    gtagConsentUpdate({
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied",
    });
    clarityConsentUpdate(false);
  };

  if (!showBanner) {
    // Show a floating settings button after the user has interacted
    if (hasInteracted) {
      return (
        <button
          onClick={() => window.dispatchEvent(new CustomEvent("open-cookie-consent"))}
          className="cookie-settings-btn"
          aria-label="Cookie Preferences"
          title="Cookie Preferences"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5" />
            <path d="M8.5 8.5a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1" />
            <path d="M15.5 14.5a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1" />
            <path d="M11 13.5a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1" />
          </svg>
        </button>
      );
    }
    return null;
  }

  return (
    <div className="cookie-banner-overlay anim-slide-up">
      <div className="card cookie-card">
        <div className="cookie-header">
          <ShieldCheck className="cookie-icon" size={24} />
          {/* Not a heading: the consent overlay sits outside the page's
              document outline, and an h4 here broke Lighthouse's
              sequential-heading-order audit on every page. */}
          <p className="cookie-title">Cookie Consent</p>
        </div>
        <p className="cookie-text">
          We use cookies to personalize content and ads, and to analyze our traffic. We share information about your use of our site with our advertising and analytics partners.
        </p>
        <div className="cookie-buttons">
          <button onClick={handleDecline} className="btn btn-secondary btn-sm-padding">
            Decline
          </button>
          <button onClick={handleAccept} className="btn btn-primary btn-sm-padding">
            Accept Cookies
          </button>
        </div>
      </div>

      <style jsx>{`
        .cookie-settings-btn {
          position: fixed;
          left: var(--space-4);
          bottom: var(--space-4);
          z-index: 99;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: 1px solid var(--border-color-hover);
          background-color: var(--bg-surface);
          color: var(--text-secondary);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0.65;
          transition: opacity var(--transition-fast);
          box-shadow: var(--shadow-md);
        }
        .cookie-settings-btn:hover {
          opacity: 1;
        }
        .cookie-banner-overlay {
          position: fixed;
          right: var(--space-6);
          bottom: var(--space-6);
          left: auto;
          transform: none;
          z-index: 100;
          width: min(calc(100vw - 3rem), 450px);
          max-width: 450px;
        }
        @media (max-width: 640px) {
          .cookie-banner-overlay {
            right: var(--space-4);
            bottom: var(--space-4);
            width: min(calc(100vw - 2rem), 420px);
          }
          .cookie-buttons {
            flex-direction: column;
          }
          .cookie-buttons button {
            width: 100%;
          }
        }
        .cookie-card {
          padding: var(--space-5) !important;
          background-color: var(--bg-surface);
          border: 1px solid var(--border-color-hover);
          box-shadow: var(--shadow-xl);
        }
        .cookie-header {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          margin-bottom: var(--space-2);
        }
        .cookie-icon {
          color: var(--primary);
        }
        .cookie-title {
          font-size: var(--text-md);
          font-weight: var(--fw-bold);
          color: var(--text-primary);
          margin-bottom: 0;
        }
        .cookie-text {
          font-size: var(--text-xs);
          line-height: 1.5;
          color: var(--text-secondary);
          margin-bottom: var(--space-4);
        }
        .cookie-buttons {
          display: flex;
          justify-content: flex-end;
          gap: var(--space-3);
        }
        .btn-sm-padding {
          padding: 0.5rem 1rem !important;
          font-size: var(--text-sm) !important;
        }
      `}</style>
    </div>
  );
}
